import { formatDateTime, t } from "@hakili/i18n";
import { useEffect, useState } from "react";
import { BOUTON_TEXTE } from "@hakili/ui";

/**
 * Round `WF-CON-11-NEW-BANK-PIPELINE`, `M1` (`OD-42`, WF-CON-11 amended): **« Recommandations » , the chargé's
 * panel of proposed sends.** A bank joined the panel after her files were submitted; Hakili proposes the ones
 * that fit its policy, each with the filter's own sentence, and **she approves or dismisses each: nothing is
 * ever sent without her act.**
 *
 * **Chargé-facing ONLY** (I8, the architect's correction of the amendment's wording): this is the console's
 * `/recommandations`, a detail of `collecte`; no screen of the bank's own app reads a proposal, and the
 * empty state below lives here and nowhere else. The panel is never blank: no proposal says « Aucun dossier
 * recommandé pour l'instant » , the founder's sentence, in his words.
 *
 * The refusal of a door , a file that no longer fits the bank, a proposal already decided , is the server's
 * sentence, shown as it came (6.5.1): the screen never looks up a different one than the refusal that happened.
 */

export interface ProposalView {
  readonly id: string;
  readonly dossierId: string;
  readonly reference: string;
  readonly clientName: string;
  readonly vehicleLabel: string;
  readonly bankName: string;
  readonly reason: string;
  readonly quotitePct: number;
  readonly proposedAt: string;
}

export interface RecommandationsDoors {
  readonly readProposals: () => Promise<readonly ProposalView[]>;
  readonly approve: (id: string) => Promise<void>;
  readonly dismiss: (id: string) => Promise<void>;
}

export interface RecommandationsProps {
  readonly proposals: readonly ProposalView[];
  readonly busy: boolean;
  readonly refusal: string | null;
  readonly onApprove: (id: string) => void;
  readonly onDismiss: (id: string) => void;
  readonly onOpenDossier: (dossierId: string) => void;
}

export function Recommandations(props: RecommandationsProps): React.JSX.Element {
  return (
    <main className="flex-1 overflow-y-auto">
      <div>
        <div className="mb-6">
          <h1 className="text-2xl font-semibold tracking-tight">{t("console.recommandations.titre")}</h1>
          <p className="mt-1.5 text-sm text-gray-500">{t("console.recommandations.sousTitre")}</p>
        </div>

        {props.refusal === null ? null : (
          <p role="alert" data-testid="recommandations-refus" className="mb-3 text-sm text-rose-600">
            {props.refusal}
          </p>
        )}

        {props.proposals.length === 0 ? (
          <p
            data-testid="recommandations-vide"
            className="rounded-2xl border border-gray-200 bg-white px-5 py-6 text-sm text-gray-500"
          >
            {t("console.recommandations.vide")}
          </p>
        ) : (
          <ul className="space-y-3">
            {props.proposals.map((proposal) => (
              <li
                key={proposal.id}
                data-testid={`recommandation-${proposal.reference}-${proposal.bankName}`}
                className="overflow-hidden rounded-2xl border border-gray-200 bg-white"
              >
                <div className="flex items-start gap-4 border-b border-gray-100 px-5 py-3.5">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900">{proposal.bankName}</p>
                    <p className="text-xs text-gray-500">
                      {proposal.clientName} · {proposal.reference} · {proposal.vehicleLabel}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-sm font-medium tabular-nums">
                      {t("console.recommandations.quotite")} {String(proposal.quotitePct)} %
                    </p>
                    <p className="text-xs text-gray-500">{formatDateTime(new Date(proposal.proposedAt))}</p>
                  </div>
                </div>
                <div className="px-5 py-3.5">
                  <p className="text-xs font-medium text-gray-500">{t("console.recommandations.motif")}</p>
                  <p className="mt-0.5 text-sm text-gray-900" data-testid="recommandation-motif">
                    {proposal.reason}
                  </p>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      data-testid="recommandation-envoyer"
                      disabled={props.busy}
                      onClick={() => {
                        props.onApprove(proposal.id);
                      }}
                      className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50"
                    >
                      {t("console.recommandations.envoyer")}
                    </button>
                    <button
                      type="button"
                      data-testid="recommandation-ecarter"
                      disabled={props.busy}
                      onClick={() => {
                        props.onDismiss(proposal.id);
                      }}
                      className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 disabled:opacity-50"
                    >
                      {t("console.recommandations.ecarter")}
                    </button>
                    <button
                      type="button"
                      data-testid="recommandation-ouvrir"
                      onClick={() => {
                        props.onOpenDossier(proposal.dossierId);
                      }}
                      className={`text-sm ${BOUTON_TEXTE}`}
                    >
                      {t("console.recommandations.ouvrir")}
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </main>
  );
}

const failureWords = (failure: unknown): string =>
  (failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee");

export interface RecommandationsScreenProps extends RecommandationsDoors {
  readonly onOpenDossier: (dossierId: string) => void;
}

/** The screen: reads the doors' list, acts through them, re-reads after every act. */
export function RecommandationsScreen(props: RecommandationsScreenProps): React.JSX.Element | null {
  const [proposals, setProposals] = useState<readonly ProposalView[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [epoch, setEpoch] = useState(0);
  const { readProposals } = props;

  useEffect(() => {
    const run = { live: true };
    void (async () => {
      try {
        const read = await readProposals();
        if (run.live) setProposals(read);
      } catch (failure) {
        if (run.live) setRefusal(failureWords(failure));
      }
    })();
    return () => {
      run.live = false;
    };
  }, [readProposals, epoch]);

  if (proposals === null) {
    return refusal === null ? null : (
      <p role="alert" data-testid="recommandations-refus" className="text-sm text-rose-600">
        {refusal}
      </p>
    );
  }

  const act = (door: (id: string) => Promise<void>, id: string): void => {
    void (async () => {
      setBusy(true);
      setRefusal(null);
      try {
        await door(id);
      } catch (failure) {
        setRefusal(failureWords(failure));
      } finally {
        setBusy(false);
        // Re-read either way: a proposal refused as already decided, or no longer true, has a list to show.
        setEpoch((one) => one + 1);
      }
    })();
  };

  return (
    <Recommandations
      proposals={proposals}
      busy={busy}
      refusal={refusal}
      onApprove={(id) => {
        act(props.approve, id);
      }}
      onDismiss={(id) => {
        act(props.dismiss, id);
      }}
      onOpenDossier={props.onOpenDossier}
    />
  );
}
